import { VALIDATION_ERROR_KEYS } from "@/i18n"
import { attachmentSchema } from "@/lib/zod-schemas/attachment-schema"
import * as z from "zod"

// Helper schemas for reusable patterns
const requiredString = z.string().min(1, VALIDATION_ERROR_KEYS.REQUIRED)

const yearString = requiredString.regex(
  /^\d{4}$/,
  VALIDATION_ERROR_KEYS.MUST_BE_VALID_YEAR,
)

// Mobile number validation pattern
const mobileNumberSchema = z
  .string()
  .regex(
    /^\+?\d{1,3}-?\d{1,14}$/,
    "listing_profiles:form.general_information_section.personal_information.validation.mobile_number_invalid",
  )
  .nullable()
  .or(z.literal(""))

// Teaching style rating schema (1-5 scale)
const teachingStyleRating = z.number().nullable()

// Address details schema for residential address
const addressDetailsSchema = z.object({
  formatted_address: z.string().optional().nullable(),
  place_id: z.string().optional().nullable(),
  lat: z.number().optional().nullable(),
  lng: z.number().optional().nullable(),
})

// Accreditation schemas
const otherApprovalSchema = z.object({
  rego_number: requiredString,
  description: requiredString,
})

// Education qualification schema
const educationQualificationSchema = z.object({
  qualification: requiredString,
  university_name: requiredString,
  year_obtained: yearString,
})

const yearValueRange = z.object({
  from: yearString,
  to: yearString,
})

// Teaching experience schema
const teachingExperienceSchema = z.object({
  subject: requiredString,
  year_level: requiredString,
  year: yearValueRange,
})

// Work experience schema
const workExperienceSchema = z.object({
  company_name: requiredString,
  title: requiredString,
  year: yearValueRange,
})

// Membership schema
const membershipSchema = z.object({
  membership: requiredString,
  link: requiredString,
})

// Assessor/Examiner subject schema
const experienceSubjectSchema = z.object({
  subject_key: z
    .string()
    .nullable()
    .optional()
    .refine((val) => val !== null && val !== undefined, {
      message: VALIDATION_ERROR_KEYS.REQUIRED,
    }),
  year: yearValueRange,
})

// Students taught attributes schema
const studentsTaughtSchema = z.object({
  gender_keys: z.array(z.string()),
  gender_notes: z.string().nullable(),
  religion_keys: z.array(z.string()),
  religion_note: z.string().nullable(),
  primary_language_keys: z.array(z.string()),
  primary_language_notes: z.string().nullable(),
  secondary_language_keys: z.array(z.string()),
  secondary_language_notes: z.string().nullable(),
  eyesights: z.array(z.string()),
  eyesight_notes: z.string().nullable(),
  adhds: z.array(z.string()),
  adhd_notes: z.string().nullable(),
  depressions: z.array(z.string()),
  depression_notes: z.string().nullable(),
  intellectual_ability_notes: z.string().nullable(),
  socioeconomic_notes: z.string().nullable(),
  other_notes: z.string().nullable(),
})

// Teaching style attributes schema
const teachingStyleSchema = z.object({
  style_autocratic: teachingStyleRating,
  style_reserved: teachingStyleRating,
  style_talk: teachingStyleRating,
  style_consultative: teachingStyleRating,
  style_participative: teachingStyleRating,
  style_comment: z.string().nullable(),
})

// Experience attributes schema
const experienceAttributesSchema = z.object({
  education_qualifications_attributes: z.array(educationQualificationSchema),
  teaching_experiences_attributes: z.array(teachingExperienceSchema),
  work_experiences_attributes: z.array(workExperienceSchema),
  memberships_attributes: z.array(membershipSchema),
  teacher_experience_assessor_subjects_attributes: z.array(
    experienceSubjectSchema,
  ),
  teacher_experience_examiner_subjects_attributes: z.array(
    experienceSubjectSchema,
  ),
  students_taught_attributes: studentsTaughtSchema.nullable(),
})

// Accreditation attributes schema
const accreditationAttributesSchema = z.object({
  vic_approval_rego_number: z.string().nullable(),
  nsw_approval_rego_number: z.string().nullable(),
  qld_approval_rego_number: z.string().nullable(),
  wa_approval_rego_number: z.string().nullable(),
  sa_approval_rego_number: z.string().nullable(),
  tas_approval_rego_number: z.string().nullable(),
  nt_approval_rego_number: z.string().nullable(),
  act_approval_rego_number: z.string().nullable(),
  other_approvals_attributes: z.array(otherApprovalSchema),
})

// Teacher profile schema
const teacherProfileSchema = z.object({
  // Personal Information
  first_name: requiredString,
  last_name: requiredString,
  dob: z.string().nullable(),
  gender_key: z.string().nullable(),
  religion_key: z.string().nullable(),
  race_key: z.string().nullable(),
  primary_language_key: z.string().nullable(),
  secondary_language_key: z.string().nullable(),
  citizenship_country_sacc_codes: z
    .array(z.string())
    .max(
      2,
      "listing_profiles:form.general_information_section.personal_information.validation.citizenship_max_two",
    ),
  mobile: mobileNumberSchema,
  origin_country_sacc_code: z.string().nullable(),

  // Residential Address
  residential_address_details: addressDetailsSchema.nullable(),

  // Background Video
  background_video_url: z.string().nullable(),

  // Accreditation
  accreditation_attributes: accreditationAttributesSchema,

  // Experience
  experience_attributes: experienceAttributesSchema,

  // Teaching Style
  teaching_style_attributes: teachingStyleSchema,
})

// Teacher profile attachments schema
const teacherProfileAttachmentsSchema = z.object({
  teacher_profile_photo_id: attachmentSchema,
  teacher_profile_resume: attachmentSchema,
  other_approval_photo_ids: z.array(attachmentSchema),
})

// Listing profile schema
const listingProfileSchema = z.object({
  name: requiredString,
  visible: z.boolean().optional(),
  webpage_url: z
    .url(VALIDATION_ERROR_KEYS.MUST_BE_VALID_URL)
    .nullable()
    .or(z.literal("")),
  video_url: z
    .url(VALIDATION_ERROR_KEYS.MUST_BE_VALID_URL)
    .nullable()
    .or(z.literal("")),
  written_comment: z.string().nullable(),
  tag_ids: z.array(z.string()),
})

// Complete Personal Listing Profile schema
export const personalListingProfileSchema = z.object({
  listing_profile: listingProfileSchema,
  teacher_profile: teacherProfileSchema,
  teacher_profile_attachments: teacherProfileAttachmentsSchema,
})

export type PersonalListingProfileFormData = z.infer<
  typeof personalListingProfileSchema
>
